<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useCartStore } from '~/stores/cart'
import { formatPrice, getImageUrl } from '~/utils/transformations'
import { displayTypeCatName } from '~/utils/products'

const cart = useCartStore()
const { items, totalPrice } = storeToRefs(cart)

const emit = defineEmits<{ close: [] }>()
const localePath = useLocalePath();
const { t, locale } = useI18n()

</script>

<template>
  <div id="cart">
    <button class="close-btn" @click="emit('close')" :aria-label="t('cart.close')">×</button>
    <Icon name="tabler:shopping-cart" size="1.5rem" style="color: var(--dark-blue)" />
    <h2 class="bottom-border">{{ t('cart.title') }}</h2>
    <p v-if="!items.length" class="empty-cart">{{ t('cart.empty') }}</p>
    <ul v-else class="products">
      <li v-for="item in items" :key="item.id" class="product-row">
        <div class="image-col">
          <img
            :src="item.image ? getImageUrl(item.image) : '/images/default/image-not-available.avif'"
            :alt="item.name"
            width="1024"
            height="1024"
          />
        </div>

        <div class="details-col">
          <div class="product-name">{{ item.name }}</div>
          <span v-if="item.typeCatName && item.norm && item.norm > 1" class="product-pill">
            {{ t('cart.packPill', { typeCat: displayTypeCatName(item.typeCatName, locale), count: item.norm }) }}
          </span>
          <div class="product-qty">
            <QtyControls
              size="compact"
              :quantity="cart.getDisplayQuantity(item.id)"
              :type-cat-name="item.typeCatName"
              :norm="item.norm"
              @increase="cart.changeQuantity(item.id, 1)"
              @decrease="cart.changeQuantity(item.id, -1)"
              @remove="cart.removeProduct(item.id)"
            />
            <div class="product-price">{{ formatPrice(cart.getItemPrice(item.id)) }}</div>
          </div>
        </div>

        <div class="action-col">
          <button class="remove-btn" @click="cart.removeProduct(item.id)" :aria-label="t('cart.remove')">
            <Icon name="tabler:trash" size="1.1rem" />
          </button>
        </div>
      </li>
    </ul>

    <h2 class="top-border">{{ t('cart.total') }} <span class="dark-blue">{{ formatPrice(totalPrice) }}</span></h2>

    <div class="buttons">
      <NuxtLink class="btn-cta" :to="localePath(items.length ? '/your-cart' : '/category/voda')" @click="emit('close')">
        {{ items.length ? t('cart.order') : t('cart.addProducts') }}
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped>
#cart {
  text-align: center;
  padding: 2rem;
  border: 1px solid var(--ligth-blue);
  border-radius: 15px;
  width: 450px;
  max-width: calc(100vw - 2rem);
  max-height: calc(100dvh - 90px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  margin: 1rem auto;
  background-color: var(--white);
  position: relative;
}

.close-btn {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background-color: var(--ligth-blue);
  color: var(--dark-blue);
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  z-index: 1;
}

h2  {
  text-align: center;
}

.bottom-border {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--ligth-blue);
}

.top-border {
  padding-top: 0.75rem;
  border-top: 1px solid var(--ligth-blue);
}

.products {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  text-align: left;
}

.empty-cart {
  padding: 2rem 0;
  color: var(--dark-blue);
  text-align: center;
}

.product-row {
  display: grid;
  grid-template-columns: 64px 1fr 28px;
  gap: 8px;
  align-items: start;
  padding: 12px 0 12px 8px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  position: relative;
}

.image-col img {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
}

.details-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-start;
  min-width: 0;
}

.product-name {
  font-weight: 600;
}

.product-price {
  color: var(--dark-blue);
  font-size: 1rem;
  font-weight: 400;
  white-space: nowrap;
}

.product-pill {
  width: fit-content;
  background-color: var(--ligth-blue);
  color: var(--dark-blue);
  font-size: 0.8rem;
  padding: 2px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.product-qty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 0.95rem;
}

.action-col {
  position: relative;
  display: block;
}

.remove-btn {
  position: absolute;
  top: 0;
  right: 0;
  background: transparent;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  color: #888;
  padding: 4px;
}

.remove-btn:hover {
  color: #333;
}

.buttons {
  display: flex;
  gap: 12px;
  margin-top: 1rem;
  justify-content: center;
}

@media (max-width: 480px) {
  #cart {
    padding: 1.25rem 1rem;
  }

  .product-row {
    grid-template-columns: 56px 1fr 24px;
  }

  .product-price {
    white-space: normal;
  }
}
</style>